import { useEffect, useRef } from 'react';

import { AlertCircle, CheckCircle2 } from 'lucide-react';

import POSInputField from '../../../POS/Fields/POSInputField';

import { formatCurrency } from '../../../../utils/formatCurrency';
import { useCurrencyInput } from '../../../../hooks/useCurrencyInput';

interface Props {
  payment: any;

  totalToPay: number;

  onChange: (payment: any) => void;

  /** Enter en "Efectivo recibido" dispara esto (Finalizar/Enviar a Caja). */
  onEnter?: () => void;
}

const POSPaymentItemSection: React.FC<Props> = ({
  payment,
  totalToPay,
  onChange,
  onEnter,
}) => {
  // =====================================================
  // 🔹 States
  // =====================================================
  const isCash = payment.paymentMethodCode === '01';

  // =====================================================
  // 🔹 Helpers
  // =====================================================
  const normalizeMoney = (value: any): number => {
    const amount = Number(value || 0);

    return Math.round((amount + Number.EPSILON) * 100) / 100;
  };

  const normalize = (value: any) => {
    if (value === '' || value === undefined || value === null) {
      return '';
    }

    return normalizeMoney(value);
  };

  // =====================================================
  // 🔹 Totals
  // =====================================================

  /**
   * ✅ Total comercial visible.
   *
   * Backend ya sincroniza el total
   * comercialmente redondeado.
   */
  const normalizedTotal = normalizeMoney(totalToPay);

  const autoAmount = normalizedTotal;

  // =====================================================
  // 🔹 Sync Auto Amount
  // =====================================================
  useEffect(() => {
    if (!isCash) {
      if (payment.amount !== autoAmount) {
        onChange({
          ...payment,

          amount: autoAmount,
        });
      }
    }
  }, [autoAmount, isCash, payment, onChange]);

  // =====================================================
  // 💵 Efectivo recibido = total a pagar por defecto
  // =====================================================
  // La mayoría de clientes paga con el monto exacto; prellenamos
  // "Efectivo recibido" con el total para que el cajero no tenga que
  // escribirlo. Si el cajero lo edita (ej. el cliente entregó $20 en vez
  // de los $10 que marca la factura), ese valor manual ya no se vuelve a
  // sobrescribir — solo seguimos sincronizando mientras el campo siga
  // reflejando el último monto que nosotros mismos prellenamos.
  const lastAutoCashRef = useRef<number | null>(
    typeof payment.cashReceived === 'number' ? payment.cashReceived : null
  );

  useEffect(() => {
    if (!isCash) return;

    const current = payment.cashReceived;
    const untouched =
      current === '' ||
      current === undefined ||
      current === lastAutoCashRef.current;

    if (untouched && current !== normalizedTotal) {
      lastAutoCashRef.current = normalizedTotal;

      onChange({
        ...payment,
        cashReceived: normalizedTotal,
        changeAmount: 0,
        amount: normalizedTotal,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCash, normalizedTotal, payment.cashReceived]);

  // =====================================================
  // 🔹 Cash Logic
  // =====================================================

  const safeCash =
    payment.cashReceived === '' || payment.cashReceived === undefined
      ? ''
      : normalize(payment.cashReceived);

  /**
   * ✅ Cambio comercial visible
   */
  const computedChange =
    safeCash !== ''
      ? normalizeMoney((safeCash as number) - normalizedTotal)
      : '';

  const showFeedback = safeCash !== '' && Number(safeCash) >= 0;

  const isInsufficient =
    showFeedback && normalizeMoney(safeCash as number) < normalizedTotal;

  const isEnough =
    showFeedback && normalizeMoney(safeCash as number) >= normalizedTotal;

  // =====================================================
  // 💵 Efectivo recibido — formato legible (6789 -> 6,789.00)
  // =====================================================
  const cashReceivedInput = useCurrencyInput(
    payment.cashReceived,
    (received, isEmpty) => {
      if (isEmpty) {
        onChange({
          ...payment,
          cashReceived: '',
          changeAmount: 0,
          amount: 0,
        });

        return;
      }

      const change =
        received >= normalizedTotal
          ? normalizeMoney(received - normalizedTotal)
          : 0;

      onChange({
        ...payment,
        cashReceived: received,
        changeAmount: change,
        amount: normalizedTotal,
      });
    }
  );

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div
      className="
      rounded-xl

      border
      border-stroke

      bg-whiten

      p-4

      shadow-card

      dark:border-strokedark
      dark:bg-meta-4
    "
    >
      {/* ===================================================== */}
      {/* 🔹 CASH */}
      {/* ===================================================== */}

      {isCash && (
        <div className="space-y-4">
          {/* ===================================================== */}
          {/* 🔹 Total To Pay */}
          {/* ===================================================== */}

          <div
            className="
        flex
        items-center
        justify-between

        rounded-xl

        border
        border-stroke

        bg-whiter

        px-4
        py-3

        dark:border-strokedark
        dark:bg-boxdark
      "
          >
            <div>
              <p
                className="
            text-[11px]
            font-medium
            uppercase
            tracking-wide

            text-body
          "
              >
                Total a pagar
              </p>

              <p
                className="
            mt-1

            text-xl
            font-bold

            text-primary
          "
              >
                ${formatCurrency(normalizedTotal)}
              </p>
            </div>

            <div
              className="
          rounded-lg

          border
          border-primary/20

          bg-primary/10

          px-3
          py-1

          text-[11px]
          font-semibold
          tracking-wide

          text-primary
        "
            >
              EFECTIVO
            </div>
          </div>

          {/* ===================================================== */}
          {/* 🔹 Inputs */}
          {/* ===================================================== */}

          <div className="grid gap-4">
            <POSInputField
              label="Efectivo recibido"
              name="cashReceived"
              icon="dollar"
              type="text"
              inputMode="decimal"
              value={cashReceivedInput.displayValue}
              onFocus={cashReceivedInput.onFocus}
              onBlur={cashReceivedInput.onBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && onEnter) {
                  e.preventDefault();
                  onEnter();
                  return;
                }
                cashReceivedInput.onKeyDown(e);
              }}
              onChange={(e) =>
                cashReceivedInput.onChangeText(e.target.value)
              }
            />

            <POSInputField
              label="Cambio"
              name="change"
              icon="dollar"
              type="text"
              value={
                computedChange !== ''
                  ? `$${formatCurrency(computedChange)}`
                  : '$0.00'
              }
              disabled
              onChange={() => {}}
            />
          </div>

          {/* ===================================================== */}
          {/* 🔹 Feedback */}
          {/* ===================================================== */}

          {showFeedback && (
            <div
              className={`
          flex
          items-center
          gap-3

          rounded-xl

          border

          px-4
          py-3

          text-sm
          font-medium

          transition-all

          ${
            isInsufficient
              ? `
                border-meta-1/20
                bg-meta-1
                text-white
              `
              : ''
          }

          ${
            isEnough
              ? `
                border-meta-3/20
                bg-meta-3
                text-white
              `
              : ''
          }
        `}
            >
              {isInsufficient && (
                <>
                  <AlertCircle size={18} />
                  <span>Efectivo insuficiente</span>
                </>
              )}

              {isEnough && (
                <>
                  <CheckCircle2 size={18} />
                  <span>Pago correcto</span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===================================================== */}
      {/* 🔹 OTHER METHODS */}
      {/* ===================================================== */}

      {!isCash && (
        <div className="grid gap-4">
          <POSInputField
            label="Monto"
            name="amount"
            icon="dollar"
            type="number"
            value={formatCurrency(autoAmount)}
            disabled
            onChange={() => {}}
          />

          <POSInputField
            label="Referencia"
            name="reference"
            icon="payment"
            type="text"
            placeholder="Número de referencia"
            value={payment.reference ?? ''}
            onChange={(e) =>
              onChange({
                ...payment,
                reference: e.target.value,
                amount: autoAmount,
              })
            }
          />
        </div>
      )}
    </div>
  );
};

export default POSPaymentItemSection;
